// @ts-nocheck
import dayjs from 'dayjs';
import { DEFAULT_DEBOUNCE_DELAY, INTEGER_PATTERN } from 'common/config';
import { IGroupedSupplyListItem } from 'common/models';
import { ISupply } from 'entities/Supply/Supply.models';
import { getUserName } from 'entities/User/User.helper';

export const toDataSourceMapper = <T>(data?: T[] | null): T[] | undefined =>
  data?.map((item, index) => ({ key: index, ...item }));

export const getId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export const isInteger = (number: number) => INTEGER_PATTERN.test(number.toString());

export const debounce = <A = unknown, R = void>(func: (args: A) => R) => {
  let timeoutId: NodeJS.Timeout;

  return (args: A) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func(args);
    }, DEFAULT_DEBOUNCE_DELAY);
  };
};

export const stringToNumber = (string: string) => Number(string);

export const numberToString = (number: number) => number.toString();

export const mapToMenuWithDivider = <T>(list: T[]): T[] => {
  return list.reduce<T[]>((acc, curr, index) => {
    if (index > 0) {
      acc.push({ type: 'divider' } as T);
    }

    acc.push(curr);

    return acc;
  }, []);
};

export const generateUid = () => Math.random().toString(16).slice(2);

export const groupList = {
  byCreatedAt: <T extends { createdAt: string }>(list: T[]) => {
    return list.reduce((acc: { createdAt: string; dataSource: T[] }[], item) => {
      const { createdAt } = item;
      const existingGroup = acc.find((group) => dayjs(createdAt).isSame(group.createdAt, 'day'));

      if (existingGroup) {
        existingGroup.dataSource.push(item);
      } else {
        acc.push({
          createdAt,
          dataSource: [item],
        });
      }

      return acc;
    }, []);
  },
  byUpdatedAt: <T extends { updatedAt: string }>(list: T[]) => {
    return list.reduce((acc: { updatedAt: string; dataSource: T[] }[], item) => {
      const { updatedAt } = item;
      const existingGroup = acc.find((group) => dayjs(updatedAt).isSame(group.updatedAt, 'day'));

      if (existingGroup) {
        existingGroup.dataSource.push(item);
      } else {
        acc.push({
          updatedAt,
          dataSource: [item],
        });
      }

      return acc;
    }, []);
  },
  byDeliveryDate: <T extends { deliveryDate: string | null }>(list: T[]) => {
    return list.reduce((acc: { deliveryDate: string | null; dataSource: T[] }[], item) => {
      const { deliveryDate } = item;
      const existingGroup = acc.find((group) => deliveryDate === null || dayjs(deliveryDate).isSame(group.deliveryDate, 'day'));

      if (existingGroup) {
        existingGroup.dataSource.push(item);
      } else {
        acc.push({
          deliveryDate,
          dataSource: [item],
        });
      }

      return acc;
    }, []);
  },
  byAddressLegalUser: <
    T extends {
      deliveryDate?: string | null;
      updatedAt?: string;
      supplies: ISupply[];
    },
  >(
    list: T[],
  ): IGroupedSupplyListItem[] => {
    return list.map((group) => {
      const groupedSupplies = group.supplies.reduce((acc: any, supply) => {
        const { address, legalName, user, account, subdivisionName } = supply;
        const groupKey = `${address?.name}|${legalName}|${getUserName.fullName(user.lastName, user.firstName, user.patronymic)}`;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            address,
            legalName,
            user,
            account,
            subdivisionName,
            dataSource: [],
          };
        }

        acc[groupKey].dataSource.push(supply);

        return acc;
      }, []);

      return {
        deliveryDate: group.deliveryDate,
        updatedAt: group.updatedAt,
        dataSource: Object.values(groupedSupplies),
      };
    });
  },
};

export const objectIsEmpty = (obj: any) => {
  return !Object.keys(obj).length;
};

export const formatJsonStringToCompact = (text: string) => {
  try {
    const json = JSON.parse(text);
    return JSON.stringify(json);
  } catch {
    return text.replace(/\s+/g, ' ').trim();
  }
};

export const getEmail = (email?: string | null) => {
  return email ? email : 'Электронный адрес не указан';
};

export const copyText = (text: string): void => {
  navigator.clipboard.writeText(text);
};

export const matchesSearch = (string: string, search: string): boolean => {
  return string.toLowerCase().includes(search.toLowerCase());
};
